import { useState, useCallback } from 'react';
import { searchApi } from '../services/search-api';
import type { SearchQuery, SearchHit, SearchMeta, SearchFilters } from '../types';

interface UseSearchReturn {
  /** Current search results */
  results: SearchHit[];
  /** Pagination metadata */
  meta: SearchMeta | null;
  /** Whether a search request is in flight */
  isLoading: boolean;
  /** Error message if request failed */
  error: string | null;
  /** Execute a search with the given query and filters */
  executeSearch: (query: string, filters?: SearchFilters, page?: number) => Promise<void>;
  /** Load the next page of results */
  nextPage: () => Promise<void>;
  /** Load the previous page of results */
  prevPage: () => Promise<void>;
  /** Go to a specific page */
  goToPage: (page: number) => Promise<void>;
  /** Clear results and reset state */
  clear: () => void;
}

/**
 * Hook for full search functionality with pagination and filters.
 */
export function useSearch(pageSize = 10): UseSearchReturn {
  const [results, setResults] = useState<SearchHit[]>([]);
  const [meta, setMeta] = useState<SearchMeta | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastQuery, setLastQuery] = useState<SearchQuery>({});

  const performSearch = useCallback(async (query: SearchQuery) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await searchApi.search(query);
      setResults(response.data);
      setMeta(response.meta);
      setLastQuery(query);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed');
      setResults([]);
      setMeta(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const executeSearch = useCallback(
    async (query: string, filters?: SearchFilters, page = 1) => {
      const searchQuery: SearchQuery = {
        q: query || undefined,
        page,
        pageSize,
        ...filters,
      };
      await performSearch(searchQuery);
    },
    [pageSize, performSearch],
  );

  const nextPage = useCallback(async () => {
    if (meta?.hasNextPage) {
      await performSearch({ ...lastQuery, page: (meta.page || 1) + 1 });
    }
  }, [meta, lastQuery, performSearch]);

  const prevPage = useCallback(async () => {
    if (meta?.hasPreviousPage) {
      await performSearch({ ...lastQuery, page: (meta.page || 2) - 1 });
    }
  }, [meta, lastQuery, performSearch]);

  const goToPage = useCallback(
    async (page: number) => {
      await performSearch({ ...lastQuery, page });
    },
    [lastQuery, performSearch],
  );

  const clear = useCallback(() => {
    setResults([]);
    setMeta(null);
    setError(null);
    setLastQuery({});
  }, []);

  return {
    results,
    meta,
    isLoading,
    error,
    executeSearch,
    nextPage,
    prevPage,
    goToPage,
    clear,
  };
}
